import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { ToastProvider } from '@citiguide/ui-components';
import { useEffect } from 'react';
import i18n from './i18n/config';
import { useLanguageStore } from './store/languageStore';
import { initializeAnalytics } from './utils/analytics';
import { initializeFacebook } from './utils/facebook';
import { useBrandTheme } from './hooks/useBrandTheme';
import MapPage from './pages/MapPage';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function LanguageSync() {
  const { currentLanguage } = useLanguageStore();

  useEffect(() => {
    i18n.changeLanguage(currentLanguage);
  }, [currentLanguage]);

  return null;
}

function AppInitialization() {
  useBrandTheme();

  useEffect(() => {
    const gaId = import.meta.env.VITE_GA_TRACKING_ID;
    if (gaId) initializeAnalytics(gaId);
    const fbAppId = import.meta.env.VITE_FACEBOOK_APP_ID;
    if (fbAppId) initializeFacebook(fbAppId);
  }, []);

  return null;
}

/**
 * Public Frontend App - End-user interface
 * This is the public-facing map interface served on subdomain
 */
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <ToastProvider>
          <BrowserRouter>
            <AppInitialization />
            <LanguageSync />
            <Routes>
              <Route path="/" element={<MapPage />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

